import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import type { HomeOnePlatformData, HomeProductTab } from "@/lib/homepage";

type OnePlatformProps = {
  data: HomeOnePlatformData;
};

function ProductTabButton({
  tab,
  isActive,
  onClick,
}: {
  tab: HomeProductTab;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 flex flex-col items-center gap-1 p-6 transition-all duration-300 platform-tabs-nav-btn ${
        isActive ? 'opacity-100' : 'opacity-40 hover:opacity-70'
      }`}
    >
      {tab.icon ? (
        <div
          className={`text-7xl transition-colors onplexus-icon platform-tabs-nav-icon  ${
            isActive ? 'opacity-100' : 'opacity-30'
          }`}
        >
          {tab.icon}
        </div>
      ) : (
        <div className="text-5xl opacity-0">•</div>
      )}
      <div className={`text-center text-base leading-5 whitespace-pre-line transition-colors platform-tabs-nav-label ${
        isActive ? 'text-white font-semibold' : 'text-white/40 font-normal'
      }`}>
        {tab.label}
      </div>
    </button>
  );
}

const OnPlexusTabLogo = () => (
  <svg
    width="117"
    height="24"
    viewBox="0 0 117 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="h-6 w-auto"
  >
    <g clipPath="url(#clip0_99_7651)">
      <path
        d="M21.0299 0.486432C21.0299 0.359766 20.9299 0.259766 20.8033 0.259766C20.7633 0.259766 20.7266 0.273099 20.6966 0.289766C18.2666 1.67643 15.7833 1.9431 13.2699 1.51643H13.2799C12.6099 1.3931 11.9166 1.3231 11.2099 1.3231C8.49325 1.3231 6.00659 2.30977 4.08659 3.93977C3.65955 4.30237 3.26063 4.69683 2.89325 5.11977C1.29325 6.96643 0.333252 9.36643 0.333252 11.9931C0.333252 14.6198 1.26325 16.9464 2.80992 18.7798L6.55325 15.0364C5.92476 14.1462 5.58816 13.0828 5.58992 11.9931C5.58992 9.0731 7.95659 6.70977 10.8733 6.70977C12.0066 6.70977 13.0566 7.06643 13.9166 7.6731L17.6599 3.92977L17.9533 3.63643L20.9866 0.629766L20.9799 0.623099C21.0099 0.586432 21.0299 0.539766 21.0299 0.489766V0.486432Z"
        fill="url(#paint0_linear_99_7651)"
      />
      <path
        d="M0.713369 23.5131C0.713369 23.6397 0.813369 23.7397 0.940036 23.7397C0.980036 23.7397 1.0167 23.7264 1.0467 23.7097C3.4767 22.3231 5.96004 22.0564 8.47337 22.4831H8.46337C9.13337 22.6064 9.8267 22.6764 10.5334 22.6764C13.25 22.6764 15.7367 21.6897 17.6567 20.0597C18.0837 19.6971 18.4827 19.3027 18.85 18.8797C20.44 17.0331 21.4 14.6331 21.4 12.0064C21.4 9.37973 20.47 7.05306 18.9234 5.21973L15.18 8.96306C15.7867 9.82306 16.1434 10.8731 16.1434 12.0064C16.1434 14.9264 13.7767 17.2897 10.86 17.2897C9.7267 17.2897 8.6767 16.9331 7.8167 16.3264L4.07337 20.0697L3.78004 20.3631L0.746703 23.3731L0.753369 23.3797C0.723369 23.4164 0.703369 23.4631 0.703369 23.5131H0.713369Z"
        fill="url(#paint1_linear_99_7651)"
      />
      <path
        d="M63.6567 4.02344H57.8733V20.0501H60.8467V15.5934H63.6567C64.6633 15.5934 65.6067 15.3368 66.4333 14.8834C68.2267 13.9001 69.4433 11.9968 69.4433 9.80677C69.4433 6.61344 66.8533 4.02344 63.66 4.02344H63.6567ZM63.6567 12.6234H60.8467V7.0001H63.6567C65.21 7.0001 66.47 8.2601 66.47 9.81344C66.47 11.3668 65.21 12.6268 63.6567 12.6268V12.6234ZM47.06 13.3134V20.2401H44.0867V6.1701L52.2 13.7901V6.85677H55.1733V20.9234L47.06 13.3134ZM99.4333 20.3468L96.6967 16.0768L94.0133 20.3468H91.0333L95.1 13.4534L91.19 6.78344H94.19L96.74 10.8068L99.2333 6.78344H102.26L98.3533 13.4168L102.417 20.3468H99.4333ZM71.2967 20.3468V6.7301H74.21V17.4334H79.54V20.3468H71.2967ZM81.3933 20.3468V6.78344H90.1067V9.7001H84.3067V12.1068H87.2067V15.0234H84.3067V17.4334H90.1067V20.3468H81.3933Z"
        fill="white"
      />
      <path
        d="M109.287 6.48999C105.403 6.48999 102.243 9.64999 102.243 13.5367C102.243 17.4233 105.403 20.58 109.287 20.58C113.17 20.58 116.333 17.42 116.333 13.5367C116.333 9.65332 113.173 6.48999 109.287 6.48999ZM109.287 17.58C107.057 17.58 105.243 15.7667 105.243 13.5367C105.243 11.3067 107.057 9.49332 109.287 9.49332C111.517 9.49332 113.33 11.3067 113.33 13.5367C113.33 15.7667 111.517 17.58 109.287 17.58ZM33.8867 3.65332C29.2767 3.65332 25.54 7.38999 25.54 12C25.54 16.61 29.2767 20.3467 33.8867 20.3467C38.4967 20.3467 42.2334 16.61 42.2334 12C42.2334 7.38999 38.4967 3.65332 33.8867 3.65332ZM33.8867 17.2633C30.98 17.2633 28.6234 14.9067 28.6234 12C28.6234 9.09332 30.98 6.73665 33.8867 6.73665C36.7934 6.73665 39.15 9.09332 39.15 12C39.15 14.9067 36.7934 17.2633 33.8867 17.2633Z"
        fill="white"
      />
    </g>
    <defs>
      <linearGradient
        id="paint0_linear_99_7651"
        x1="7.13992"
        y1="-0.356901"
        x2="12.3833"
        y2="11.0831"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#EBC738" />
        <stop offset="0.28" stopColor="#E9C335" />
        <stop offset="0.52" stopColor="#E4B92D" />
        <stop offset="0.73" stopColor="#DCA81F" />
        <stop offset="0.93" stopColor="#D1910D" />
      </linearGradient>
      <linearGradient
        id="paint1_linear_99_7651"
        x1="13.0034"
        y1="22.2664"
        x2="9.03003"
        y2="11.1997"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#EBC738" />
        <stop offset="0.27" stopColor="#E9C335" />
        <stop offset="0.49" stopColor="#E4B92D" />
        <stop offset="0.69" stopColor="#DCA81F" />
        <stop offset="0.88" stopColor="#D1910D" />
      </linearGradient>
      <clipPath id="clip0_99_7651">
        <rect width="116.667" height="24" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

export default function OnePlatform({ data }: OnePlatformProps) {
  const fallbackId = data.products[0]?.id ?? "";
  const [activeTab, setActiveTab] = useState(fallbackId);

  useEffect(() => {
    if (fallbackId) {
      setActiveTab(fallbackId);
    }
  }, [fallbackId]);
  
  const currentTab = data.products.find((tab) => tab.id === activeTab) || data.products[0];

  if (!data.products.length || !currentTab) return null;

  return (
    <section className="py-12 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="space-y-12">
          {/* Section Header */}
          <div className="text-center space-y-6">
            <div className="space-y-2">
              <h2 className="text-2xl font-bold text-white leading-7">
                {data.heading}
              </h2>
              <p className="text-2xl text-white/70 leading-7">
                {data.subheading}
              </p>
            </div>
            
            <p className="text-xl text-white/70 leading-relaxed max-w-2xl mx-auto">
              {data.intro}
            </p>
          </div>

          {/* Tab Navigation */}
          <div className="space-y-6">
            <div className="flex flex-wrap justify-center">
              {data.products.map((tab) => (
                <ProductTabButton
                  key={tab.id}
                  tab={tab}
                  isActive={activeTab === tab.id}
                  onClick={() => setActiveTab(tab.id)}
                />
              ))}
            </div>
            
            {/* Divider */}
            <div className="flex items-center">
              <div className="w-24 h-0.5 bg-gradient-to-r from-transparent via-white/10 to-white/10"></div>
              <div className="flex-1 h-0.5 bg-white/10"></div>
              <div className="w-24 h-0.5 bg-gradient-to-l from-transparent via-white/10 to-white/10"></div>
            </div>
          </div>

          {/* Tab Content Container */}

          <div className="p-2 platformBorder-Outter">
            <div className="platformBorder-Thick">
              <div className="platformBorder-Inner">
                <div className="p-2 bg-onplexus-dark">
                  {/* Platform Header */}
                  <div className="p-2 flex items-center">
                    <OnPlexusTabLogo />
                  </div>
                  
                  {/* Content Divider */}
                  <div className="flex items-center">
                    <div className="w-24 h-px bg-gradient-to-r from-transparent via-white/10 to-white/10"></div>
                    <div className="flex-1 h-px bg-white/10"></div>
                    <div className="w-24 h-px bg-gradient-to-l from-transparent via-white/10 to-white/10"></div>
                  </div>

                  {/* Tab Content */}
                  <div className="p-4 md:p-12 grid lg:grid-cols-2 gap-6">
                  {/* Left Content */}
                  <div className="space-y-8 platformContainer-Details">
                    {/* Tag */}
                    <div className="flex items-center gap-2">
                      {currentTab.icon ? (
                        <div className="flex items-center justify-center  w-8 h-8 p-2 pt-2 rounded bg-[#D9C478]/15">
                          <span className="text-[#D9C478] text-4xl onplexus-icon pt-0.5">{currentTab.icon}</span>
                        </div>
                      ) : null}
                      <span className="text-white font-semibold">
                        {currentTab.tag}
                      </span>
                    </div>

                      {/* Content */}
                      <div className="space-y-4">
                        <h3 className="text-2xl font-bold text-white leading-7">
                          {currentTab.title}
                        </h3>
                        <p className="text-lg text-white/70 leading-relaxed">
                          {currentTab.description}
                        </p>
                      </div>

                      {/* CTA Button */}
                      <a href="#" className="flex w-fit h-[45px] pl-5 pr-3 justify-center items-center gap-1.5 rounded-xl border border-onplexus-gold text-onplexus-gold hover:bg-onplexus-gold hover:text-onplexus-dark transition-colors" >
                        <span className="font-semibold text-lg">Learn More</span>
                        <span className="onplexus-icon text-5xl pt-0.5">→</span>
                      </a>
                    </div>

                    {/* Right Image */}
                    <div className="relative">
                      {currentTab.image ? (
                        <img
                          src={currentTab.image}
                          alt={currentTab.title ? `${currentTab.title} dashboard interface` : undefined}
                          className="w-full h-auto rounded-lg"
                        />
                      ) : (
                        <div className="w-full aspect-[4/3] rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-white/50">
                          <span>Image coming soon</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
